/**
 * Hover "how this was calculated" notes on the computed QC cells — a port of OG
 * `SpreadsheetEntryController.AddQcCellComments` (2026-09-23 build, :26458).
 *
 * OG attaches a native cell COMMENT (shown on hover) to Result / %Recovery / RPD on every result row,
 * built from the values already on the sheet: the displayed Result / NumericResult / DF / RPD /
 * %Recovery / SpikeAmount cells plus the row's cross-row source values (DupSourceResult /
 * SpikeSourceResult) looked up in dtsample by SysSampleCode + Parameter. Display-only: OG never adds
 * them on a persist path, so the stored workbook stays free of comments. G6 renders them as Univer
 * NOTES (the Excel-comment equivalent) — the saved blob is patched from tracked edits only, so notes
 * never reach it either.
 *
 * OG calls it after BindGrid (Recalculate — every template) and after a Save that ran the Formula
 * Engine (bound templates only); never on the initial open. The page mirrors those two call sites.
 */

/** One note to place: 0-based row/col on the named sheet. */
export interface QcCellNote { sheetName: string; row: number; col: number; text: string }

/** A worksheet's DISPLAYED cell text (0-based rows/cols), e.g. the sheet handle's getSheetDisplayValues. */
export type SheetDisplay = (string | null)[][];

const FIELD_ALIASES: Record<string, string[]> = {
  RESULT: ['RESULT'], RECOVERY: ['%RECOVERY'], RPD: ['RPD'], QCSTATUS: ['QCSTATUS'],
  PARAMETER: ['PARAMETER'], SYS: ['SYSSAMPLECODE'], NUMERIC: ['NUMERICRESULT'], DF: ['DF'],
  SPIKEAMOUNT: ['SPIKEAMOUNT'], DUPSOURCE: ['DUPSOURCE'], SPIKESOURCE: ['SPIKESOURCE'],
  DUPSOURCERESULT: ['DUPSOURCERESULT'], SPIKESOURCERESULT: ['SPIKESOURCERESULT'],
};

function parseA1(a1?: string | null): { col: number; row: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase().replace(/\$/g, '').split('!').pop() ?? '');
  if (!m) return null;
  let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { col, row: parseInt(m[2]!, 10) };   // 1-based
}

function fieldCell(fieldCells: Record<string, string>, key: keyof typeof FIELD_ALIASES): { col: number; row: number } | null {
  for (const alias of FIELD_ALIASES[key]!) {
    const hit = Object.keys(fieldCells).find(k => k.toUpperCase() === alias);
    if (hit) { const p = parseA1(fieldCells[hit]); if (p) return p; }
  }
  return null;
}

/** OG TryDec: decimal.TryParse(Float | AllowThousands, invariant); false for "<RL"/blank/non-numeric. */
export function tryDec(s: string | null | undefined): number | null {
  if (s == null) return null;
  const t = s.trim();
  if (!t || !/^[+-]?(\d{1,3}(,\d{3})+|\d+)?(\.\d+)?([eE][+-]?\d+)?$/.test(t) || !/\d/.test(t)) return null;
  const n = Number(t.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** OG Num: d.ToString("0.######") — compact, no forced trailing zeros. */
export function num(d: number): string {
  const r = Math.round(d * 1e6) / 1e6;
  const s = (Object.is(r, -0) ? 0 : r).toFixed(6).replace(/\.?0+$/, '');
  return s === '-0' ? '0' : s;
}

const same = (a: number, b: number) => Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

function recVal(rec: Record<string, unknown> | undefined, key: string): string | null {
  if (!rec) return null;
  const k = Object.keys(rec).find(x => x.toUpperCase() === key.toUpperCase());
  const v = k == null ? null : rec[k];
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

/** OG NumOrText: a compact number when numeric, else the raw text, else null. */
function numOrText(rec: Record<string, unknown> | undefined, key: string): string | null {
  const s = recVal(rec, key);
  const d = tryDec(s);
  return d != null ? num(d) : s;
}

/**
 * Build the notes for every result sheet. `sheets` = the sheets OG walks — the first worksheet for a
 * BatchSampleEntry template (Model 0), the per-run sheets named by SysSampleCode for a
 * SampleSheetNavigation template (Model 1) — each with its DISPLAYED text. `records` = the merge
 * records (G6's dtsample) for the cross-row source values.
 */
export function buildQcNotes(
  sheets: ReadonlyArray<{ name: string; display: SheetDisplay }>,
  fieldCells: Record<string, string>,
  records: ReadonlyArray<Record<string, unknown>>,
): QcCellNote[] {
  const pResult = fieldCell(fieldCells, 'RESULT');
  const pRec = fieldCell(fieldCells, 'RECOVERY');
  const pRpd = fieldCell(fieldCells, 'RPD');
  const pQc = fieldCell(fieldCells, 'QCSTATUS');
  if (!pResult && !pRec && !pRpd && !pQc) return [];
  const startRow = (pResult ?? pRec ?? pRpd ?? pQc)!.row;   // 1-based, OG anchor = first positioned output
  const pParam = fieldCell(fieldCells, 'PARAMETER');
  const pSys = fieldCell(fieldCells, 'SYS');
  const pNum = fieldCell(fieldCells, 'NUMERIC');
  const pDf = fieldCell(fieldCells, 'DF');
  const pSpk = fieldCell(fieldCells, 'SPIKEAMOUNT');
  const pDupSrc = fieldCell(fieldCells, 'DUPSOURCE'), pSpkSrc = fieldCell(fieldCells, 'SPIKESOURCE');
  const pDupRes = fieldCell(fieldCells, 'DUPSOURCERESULT'), pSpkRes = fieldCell(fieldCells, 'SPIKESOURCERESULT');

  // Every row's DISPLAYED Result, keyed SysSampleCode|Parameter across all the walked sheets — where OG's dtsample
  // already holds DupSourceResult / SpikeSourceResult (its engine filled them), G6's merge records do not on a new
  // batch, so the source row's own Result is read off the sheet. Without it every Dup RPD note fell into the
  // "non-detect rule" wording and every MS note into the generic one (user report 2026-10-09, Alkalinity 8115).
  const shownText = (display: SheetDisplay, p: { col: number } | null, row1: number): string | null => {
    const v = p ? display[row1 - 1]?.[p.col - 1] : null;
    const t = v == null ? '' : String(v).trim();
    return t.length ? t : null;
  };
  const resultByKey = new Map<string, string>();
  if (pResult && pParam && pSys) {
    for (const { display } of sheets) {
      for (let row = startRow; row <= display.length; row++) {
        const code = shownText(display, pSys, row), param = shownText(display, pParam, row);
        const res = shownText(display, pResult, row);
        const key = `${(code ?? '').toUpperCase()}|${(param ?? '').toUpperCase()}`;
        if (code && param && res != null && !resultByKey.has(key)) resultByKey.set(key, res);
      }
    }
  }

  // dtsample source rows keyed SysSampleCode|Parameter (Parameter is REQUIRED in the key — a multi-analyte
  // test shares one "…MS1" code across Fe / Mn / Y). First row wins, as in OG.
  const srcByKey = new Map<string, Record<string, unknown>>();
  for (const r of records) {
    const code = recVal(r, 'SysSampleCode');
    if (!code) continue;
    const key = `${code.toUpperCase()}|${(recVal(r, 'Parameter') ?? '').toUpperCase()}`;
    if (!srcByKey.has(key)) srcByKey.set(key, r);
  }

  const out: QcCellNote[] = [];
  for (const { name, display } of sheets) {
    const cell = (p: { col: number } | null, row1: number): string | null => {
      if (!p) return null;
      const v = display[row1 - 1]?.[p.col - 1];
      if (v == null) return null;
      const t = String(v).trim();
      return t.length ? t : null;
    };
    const endRow = display.length;   // OG: used range bottom
    for (let row = startRow; row <= endRow; row++) {
      const parameterName = cell(pParam, row);
      if (!parameterName) continue;   // not a data row

      const result = cell(pResult, row);
      const numRes = cell(pNum, row);
      const df = cell(pDf, row);
      const rpd = cell(pRpd, row);
      const rec = cell(pRec, row);
      const sys = cell(pSys, row);
      const drow = sys ? srcByKey.get(`${sys.toUpperCase()}|${parameterName.toUpperCase()}`) : undefined;
      // R2 / the spike source: the row's own source-result cell when the template has one, else the record's value,
      // else the source row's displayed Result (the source code from the row's cell or its record).
      const sourceValue = (pRes: { col: number } | null, recKey: string, pCode: { col: number } | null, codeKey: string) => {
        const onSheet = cell(pRes, row);
        if (onSheet != null) return tryDec(onSheet) != null ? num(tryDec(onSheet)!) : onSheet;
        const fromRecord = numOrText(drow, recKey);
        if (fromRecord != null) return fromRecord;
        const code = cell(pCode, row) ?? recVal(drow, codeKey);
        const shown = code ? resultByKey.get(`${code.toUpperCase()}|${parameterName.toUpperCase()}`) : undefined;
        return shown ?? null;
      };
      const put = (p: { col: number } | null, text: string) => {
        if (p && text.trim()) out.push({ sheetName: name, row: row - 1, col: p.col - 1, text });
      };

      // Result — the real derivation; a "<…" result is a below-limit display, not the product.
      if (pResult && result != null && numRes != null) {
        if (result.trimStart().startsWith('<')) {
          put(pResult, `Result = ${result}\nNumericResult (${numRes}) is below the reporting limit.`);
        } else {
          const nrv = tryDec(numRes), dfv = tryDec(df ?? '1');
          if (nrv != null && dfv != null) {
            const prod = nrv * dfv;
            let tip = `Result = NumericResult × DF\n${numRes} × ${df ?? '1'} = ${num(prod)}`;
            const rv = tryDec(result);
            if (!(rv != null && same(rv, prod))) tip += `\nRounded to ${result}`;
            put(pResult, tip);
          }
        }
      }

      // RPD — the equation with the numbers substituted.
      if (pRpd && rpd != null) {
        const r2 = sourceValue(pDupRes, 'DupSourceResult', pDupSrc, 'DupSource');
        const a = tryDec(result), b = tryDec(r2);
        if (a != null && b != null && a + b !== 0) {
          const diff = Math.abs(a - b), avg = (a + b) / 2;
          put(pRpd, `RPD = |${result} − ${r2}| ÷ ((${result} + ${r2}) ÷ 2) × 100\n= ${num(diff)} ÷ ${num(avg)} × 100\n= ${rpd} %`);
        } else {
          put(pRpd, `RPD = |R1 − R2| ÷ ((R1 + R2) ÷ 2) × 100\n= ${rpd} %   (non-detect rule applied)`);
        }
      }

      // %Recovery — the equation with the numbers substituted.
      if (pRec && rec != null) {
        const source = sourceValue(pSpkRes, 'SpikeSourceResult', pSpkSrc, 'SpikeSource') ?? '0';
        const spkAmt = cell(pSpk, row);
        const m = tryDec(result), s = tryDec(source), sp = tryDec(spkAmt);
        if (m != null && s != null && sp != null && sp !== 0) {
          put(pRec, `%Recovery = (${result} − ${source}) ÷ ${spkAmt} × 100\n= ${num(m - s)} ÷ ${spkAmt} × 100\n= ${rec} %`);
        } else {
          put(pRec, `%Recovery = (Measured − Source) ÷ SpikeAmount × 100\n= ${rec} %`);
        }
      }
      // (No note on QCStatus — OG leaves it blank on purpose.)
    }
  }
  return out;
}
